<script setup>
// R7.4 — os detratores que chegam pelas redes sociais.
//
// O requisito nasceu pensando em COMENTÁRIO na timeline. Em 29/09 o clube decidiu não conectar
// o Facebook nem monitorar comentários, e comentário de Instagram só chega pela página do
// Facebook: a fonte original acabou. O que esta tela mostra é o que sobrou e o que o clube
// aprovou medir: quem fala mal DIRETO com o clube, por mensagem de rede social.
//
// A leitura é de cima para baixo: quantos são e se está piorando, de que reclamam, e só então a
// lista. Quem abre esta tela quer saber se tem incêndio antes de saber quem está gritando.
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import subDays from 'date-fns/subDays';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import DetratoresAPI from 'dashboard/api/fandata/detratores';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import BaixarRelatorio from './components/BaixarRelatorio.vue';
import FiltrosRelatorio from './components/FiltrosRelatorio.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import DeltaPeriodo from './DeltaPeriodo.vue';

const { t } = useI18n();
const route = useRoute();

const dados = ref(null);
const carregando = ref(true);
const erro = ref(false);
const intervalo = ref([subDays(new Date(), 6), new Date()]);
const tipoDeIntervalo = ref(DATE_RANGE_TYPES.LAST_7_DAYS);
const desde = ref(getUnixStartOfDay(subDays(new Date(), 6)));
const ate = ref(getUnixEndOfDay(new Date()));
// T-252: canal e lado. Nulo e 'todos' são o padrão: quem abre a tela vê a operação inteira.
const canalFiltrado = ref(null);

const carregar = async () => {
  carregando.value = true;
  erro.value = false;
  try {
    const { data } = await DetratoresAPI.get({
      since: desde.value,
      until: ate.value,
      inboxId: canalFiltrado.value,
    });
    dados.value = data;
  } catch (e) {
    erro.value = true;
  } finally {
    carregando.value = false;
  }
};

onMounted(carregar);

const aoTrocarPeriodo = valor => {
  const [inicio, fim, tipo] = valor;
  intervalo.value = [inicio, fim];
  tipoDeIntervalo.value = tipo || DATE_RANGE_TYPES.CUSTOM_RANGE;
  desde.value = getUnixStartOfDay(inicio);
  ate.value = getUnixEndOfDay(fim);
  carregar();
};

// Sem rede conectada a tela não tem o que dizer, e dizer "nenhum detrator" seria mentira por
// omissão: não há detrator porque não há canal, não porque ninguém reclamou.
const semCanal = computed(() => (dados.value?.canais || []).length === 0);
const temCasos = computed(() => (dados.value?.casos || []).length > 0);

// A fatia sobre o total das redes é o que impede o número de ser lido sozinho: 12 detratores em
// 20 conversas é uma crise, 12 em 800 é terça-feira.
const fatia = computed(() => {
  const total = dados.value?.total_das_redes || 0;
  if (!total) return null;
  return Math.round((dados.value.total / total) * 100);
});

const dataCurta = iso =>
  iso ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '';

const CORES_PROTOCOLO = {
  tolerancia_zero: 'text-n-ruby-11',
  vermelho: 'text-n-ruby-11',
  amarelo: 'text-n-amber-11',
};
const corDoCaso = caso => CORES_PROTOCOLO[caso.protocolo_criticidade] || 'text-n-slate-11';
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-start flex-wrap gap-3">
      <WootDatePicker
        v-model:date-range="intervalo"
        v-model:range-type="tipoDeIntervalo"
        @date-range-changed="aoTrocarPeriodo"
      />
      <!-- T-252: canal e lado. Trocar qualquer um recarrega a tela. -->
      <FiltrosRelatorio
        v-model:inbox-id="canalFiltrado"
        :com-lado="false"
        @update:inbox-id="carregar"
      />
      <BaixarRelatorio
        nome="detratores"
        :dados="dados"
        :since="desde"
        :until="ate"
      />
      <!-- T-204 (02/10): o atalho saiu por pedido da Tamara, "pois trata-se de um dado mais
         qualitativo" — a quebra por tempo não responde a pergunta desta aba. -->
    </div>

    <p class="text-sm text-n-slate-11">
      {{ $t('FAROL.DETRATORES.COMO_LER') }}
    </p>

    <div v-if="carregando" class="py-8 text-sm text-center text-n-slate-11">
      {{ $t('FAROL.DETRATORES.CARREGANDO') }}
    </div>

    <div v-else-if="erro" class="py-8 text-sm text-center text-n-ruby-11">
      {{ $t('FAROL.DETRATORES.ERRO') }}
    </div>

    <div v-else-if="semCanal" class="py-8 text-sm text-center text-n-slate-11">
      {{ $t('FAROL.DETRATORES.SEM_CANAL') }}
    </div>

    <template v-else>
      <div class="grid grid-cols-2 gap-3 md:grid-cols-3">
        <div
          class="flex flex-col gap-1 p-4 border rounded-xl border-n-weak bg-n-solid-1"
        >
          <span class="text-xs font-medium text-n-slate-11">
            {{ $t('FAROL.DETRATORES.TOTAL') }}
          </span>
          <div class="flex flex-wrap items-baseline gap-2">
            <span class="text-2xl font-semibold text-n-ruby-11 tabular-nums">
              {{ dados.total }}
            </span>
            <DeltaPeriodo
              :valor="dados.variacao ?? null"
              sufixo="%"
              melhor-quando="desce"
            />
          </div>
          <span v-if="fatia !== null" class="text-xs text-n-slate-11">
            {{
              $t('FAROL.DETRATORES.FATIA', {
                fatia,
                total: dados.total_das_redes,
              })
            }}
          </span>
        </div>

        <div
          class="flex flex-col gap-1 p-4 border rounded-xl border-n-weak bg-n-solid-1"
        >
          <span class="text-xs font-medium text-n-slate-11">
            {{ $t('FAROL.DETRATORES.ABERTOS') }}
          </span>
          <span class="text-2xl font-semibold text-n-slate-12 tabular-nums">
            {{ dados.abertos }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ $t('FAROL.DETRATORES.ABERTOS_HINT') }}
          </span>
        </div>

        <div
          class="flex flex-col gap-1 p-4 border rounded-xl border-n-weak bg-n-solid-1"
        >
          <span class="text-xs font-medium text-n-slate-11">
            {{ $t('FAROL.DETRATORES.CANAIS') }}
          </span>
          <span class="text-sm text-n-slate-12">
            {{ dados.canais.map(c => c.nome).join(', ') }}
          </span>
        </div>
      </div>

      <div v-if="dados.por_assunto.length" class="flex flex-col gap-2">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ $t('FAROL.DETRATORES.POR_ASSUNTO') }}
        </h3>
        <div class="flex flex-wrap gap-2">
          <span
            v-for="linha in dados.por_assunto"
            :key="linha.assunto || 'sem'"
            class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full bg-n-alpha-1 text-n-slate-12"
          >
            {{ linha.assunto || $t('FAROL.DETRATORES.SEM_ASSUNTO') }}
            <span class="font-semibold tabular-nums">{{ linha.total }}</span>
          </span>
        </div>
      </div>

      <div v-if="!temCasos" class="py-8 text-sm text-center text-n-slate-11">
        {{ $t('FAROL.DETRATORES.VAZIO') }}
      </div>

      <div v-else class="overflow-x-auto">
        <table class="w-full text-sm border-collapse">
          <thead>
            <tr class="text-left">
              <th class="py-2 pr-4 font-medium text-n-slate-11">
                {{ $t('FAROL.DETRATORES.TORCEDOR') }}
              </th>
              <th class="px-2 py-2 font-medium text-n-slate-11">
                {{ $t('FAROL.DETRATORES.ASSUNTO') }}
              </th>
              <th class="px-2 py-2 font-medium text-n-slate-11">
                {{ $t('FAROL.DETRATORES.CANAL') }}
              </th>
              <th class="px-2 py-2 font-medium text-right text-n-slate-11">
                {{ $t('FAROL.DETRATORES.QUANDO') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="caso in dados.casos"
              :key="caso.id"
              class="border-t border-n-weak"
            >
              <td class="py-2 pr-4 align-top">
                <!-- Abre em ABA NOVA de propósito: esta tela é de revisão, e quem está
                     passando os detratores um por um perde o lugar na lista se a conversa
                     tomar a janela. O ícone avisa o que vai acontecer antes do clique. -->
                <router-link
                  :to="{
                    name: 'inbox_conversation',
                    params: {
                      accountId: route.params.accountId,
                      conversation_id: caso.id,
                    },
                  }"
                  target="_blank"
                  rel="noopener"
                  class="inline-flex items-center gap-1 font-medium hover:underline"
                  :class="corDoCaso(caso)"
                  :title="$t('FAROL.DETRATORES.ABRIR_NOVA_ABA')"
                >
                  {{ caso.contato || caso.protocolo }}
                  <span
                    class="i-lucide-external-link size-3 shrink-0 opacity-60"
                  />
                </router-link>
                <!-- Por que esta conversa está na lista. A régua é união de três critérios, e
                     sem dizer qual valeu a pessoa não consegue conferir se concorda. -->
                <p v-if="caso.motivo" class="mt-0.5">
                  <span
                    class="inline-block px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide rounded"
                    :class="
                      caso.motivo === 'exposicao'
                        ? 'bg-n-ruby-3 text-n-ruby-11'
                        : 'bg-n-alpha-2 text-n-slate-11'
                    "
                  >
                    {{ $t(`FAROL.DETRATORES.MOTIVO.${caso.motivo.toUpperCase()}`) }}
                  </span>
                </p>
                <p
                  v-if="caso.resumo"
                  class="mt-0.5 text-xs text-n-slate-11 line-clamp-2"
                >
                  {{ caso.resumo }}
                </p>
              </td>
              <td class="px-2 py-2 align-top text-n-slate-12">
                {{ caso.assunto || '·' }}
              </td>
              <td class="px-2 py-2 align-top text-n-slate-11">
                {{ caso.canal }}
              </td>
              <td
                class="px-2 py-2 text-right align-top tabular-nums text-n-slate-11"
              >
                {{ dataCurta(caso.quando) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>
